import { useEffect, useRef, useState, useCallback } from 'react'
import { ChatMessage } from './ChatMessage'
import { DiceRollMessage } from './DiceRollMessage'
import type { FeedMessage } from '@/types'
import { isChatMessage, isDiceRollMessage } from '@/types'

interface MessageFeedProps {
  messages: FeedMessage[]
  currentUserId: string
}

// How close to the bottom (px) still counts as "parked at the bottom".
const BOTTOM_THRESHOLD = 50

export function MessageFeed({ messages, currentUserId }: MessageFeedProps) {
  const feedRef = useRef<HTMLDivElement>(null)
  // Whether the user is parked at the bottom of the feed. Drives both
  // the auto-follow behaviour and the scroll-to-bottom caret.
  const [atBottom, setAtBottom] = useState(true)

  const scrollToBottom = useCallback(() => {
    const feed = feedRef.current
    if (!feed) return
    feed.scrollTop = feed.scrollHeight
  }, [])

  // Recompute whether we're at the bottom whenever the user scrolls.
  const handleScroll = useCallback(() => {
    const feed = feedRef.current
    if (!feed) return
    const nearBottom =
      feed.scrollHeight - feed.scrollTop - feed.clientHeight < BOTTOM_THRESHOLD
    setAtBottom(nearBottom)
  }, [])

  // On initial load (history replay) and new messages, follow the bottom
  // ONLY if the user is already parked there — otherwise leave their
  // scroll position alone and surface the caret instead so they don't
  // get yanked away from older messages they're reading.
  useEffect(() => {
    if (atBottom) scrollToBottom()
  }, [messages, atBottom, scrollToBottom])

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flex: 1,
        minWidth: 0,
        height: '100%',
      }}
    >
      <div
        ref={feedRef}
        onScroll={handleScroll}
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minWidth: 0,
          height: '100%',
          overflowY: 'auto',
        }}
      >
        {messages.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              color: 'hsl(var(--ac-text-muted))',
              fontSize: '0.8125rem',
              fontStyle: 'italic',
              padding: '2rem 1rem',
            }}
          >
            No messages yet. Type /roll 1d20 to roll dice, or just type a message to chat!
          </div>
        )}
        {messages.map((msg) => {
          if (isChatMessage(msg)) {
            return (
              <ChatMessage
                key={msg.messageId}
                message={msg}
                currentUserId={currentUserId}
              />
            )
          } else if (isDiceRollMessage(msg)) {
            return (
              <DiceRollMessage
                key={msg.rollId}
                roll={msg}
                currentUserId={currentUserId}
              />
            )
          }
          return null
        })}
      </div>

      {/* Scroll-to-bottom caret — shown only while the user is scrolled
          up, so new messages don't pull them away. Gold-highlighted via
          the --ac-gm-gold token over the standard .ac-icon-btn shape. */}
      {!atBottom && (
        <button
          type="button"
          className="ac-icon-btn is-sm"
          title="Jump to latest"
          aria-label="Jump to latest messages"
          onClick={scrollToBottom}
          style={{
            position: 'absolute',
            bottom: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'hsl(var(--ac-gm-gold))',
            borderColor: 'hsl(var(--ac-gm-gold))',
            color: 'hsl(0 0% 10%)',
            boxShadow: '0 2px 8px hsl(0 0% 0% / 0.45)',
            zIndex: 2,
          }}
        >
          <svg
            width={16}
            height={16}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      )}
    </div>
  )
}
